"use client";

import { DatePicker } from "@/components/ui/date-picker";
import PageHeader from "@/components/layout/PageHeader";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider, DataTableHeader, DataTableHeaderGroup, DataTableHead, DataTableBody, DataTableRow, DataTableCell,
} from "../../components/ui/data-table";
import { get } from "../../utils/service";
import { formatDate, formatLabel, categoryLabel, isAdminUser, isFinanceUser, showMoney, showTotal, originalHint, type Paginated } from "@/utils/finance";
import StatusBadge from "@/components/finance/StatusBadge";
import StatCard from "@/components/finance/StatCard";
import Pager from "@/components/finance/Pager";
import { btnPrimary, inputCls } from "@/components/finance/Modal";

interface Expense {
  id: string; reference: string; title: string; category: string; category_other?: string | null; status: string; expense_date: string;
  original_amount: string; original_currency: string; base_amount: string; base_currency: string;
  approved_amount: string | null; amount_paid: string; remaining_amount: string | null; rejection_reason: string | null; has_receipt: boolean;
  user?: { id: string; first_name: string; last_name: string };
  display_currency?: string; display_original_amount?: string | null; display_base_amount?: string | null; display_approved_amount?: string | null; display_remaining_amount?: string | null;
}
interface Summary {
  base_currency: string; pending_approval_count: number; pending_approval_amount: string; rejected_count: number;
  awaiting_payment_count: number; total_approved: string; total_reimbursed: string; outstanding: string; draft_count: number;
  display: { currency: string; pending_approval_amount: string; total_approved: string; total_reimbursed: string; outstanding: string } | null;
}
interface Meta { base_currency: string; categories: string[]; statuses: string[]; currencies: string[] }
interface UserOpt { id: string; first_name: string; last_name: string }

export default function ExpensesPage() {
  const navigate = useNavigate();
  const admin = isFinanceUser(); // admins and managers review everyone's expenses
  const canListUsers = isAdminUser(); // /users is admin-only
  const [meta, setMeta] = useState<Meta | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [users, setUsers] = useState<UserOpt[]>([]);
  const [rows, setRows] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ scope: admin ? "all" : "mine", status: "", category: "", currency: "", user_id: "", date_from: "", date_to: "", search: "" });
  const [page, setPage] = useState(1);
  const [pager, setPager] = useState({ total: 0, total_pages: 0 });
  const set = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    get<{ data: Meta }>("/expenses/meta").then((r) => setMeta(r.data)).catch(() => undefined);
    if (canListUsers) get<Paginated<UserOpt>>("/users", { page_size: 100 }).then((r) => setUsers(r.data)).catch(() => undefined);
  }, [admin, canListUsers]);

  useEffect(() => {
    get<{ data: Summary }>("/expenses/summary", { scope: f.scope }).then((r) => setSummary(r.data)).catch(() => undefined);
  }, [f.scope]);

  useEffect(() => { setPage(1); }, [f]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      const r = await get<Paginated<Expense>>("/expenses", { ...params, page, page_size: 15 });
      setRows(r.data);
      setPager({ total: r.pagination.total, total_pages: r.pagination.total_pages });
    } finally {
      setLoading(false);
    }
  }, [f, page]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const columns: ColumnDef<Expense>[] = [
    { accessorKey: "reference", header: "Expense", cell: ({ row }) => (<div><p className="text-xs font-semibold text-gray-900">{row.original.title}</p><p className="text-xs text-gray-500">{row.original.reference} · {categoryLabel(row.original)}</p></div>) },
    ...(admin && f.scope === "all" ? [{ id: "user", header: "Submitted by", cell: ({ row }: { row: { original: Expense } }) => <span className="text-xs">{row.original.user ? `${row.original.user.first_name} ${row.original.user.last_name}` : "—"}</span> } as ColumnDef<Expense>] : []),
    { accessorKey: "expense_date", header: "Date", cell: ({ row }) => <span className="text-xs text-gray-600">{formatDate(row.original.expense_date)}</span> },
    { id: "original", header: "Paid amount", cell: ({ row }) => <div><span className="text-xs font-semibold">{showMoney(row.original, "original_amount", "original_currency")}</span>{originalHint(row.original, "original_amount", "original_currency") && <p className="text-[11px] text-gray-400">paid {originalHint(row.original, "original_amount", "original_currency")}</p>}</div> },
    { id: "base", header: "Reimbursable", cell: ({ row }) => (<div><p className="text-xs">{row.original.approved_amount ? showMoney(row.original, "approved_amount", "base_currency") : showMoney(row.original, "base_amount", "base_currency")}</p>{row.original.remaining_amount !== null && parseFloat(row.original.remaining_amount) > 0 && <p className="text-[11px] text-gray-500">{showMoney(row.original, "remaining_amount", "base_currency")} left</p>}</div>) },
    { id: "status", header: "Status", cell: ({ row }) => <StatusBadge kind="expense" status={row.original.status} /> },
  ];

  const bc = summary?.base_currency ?? "UGX";

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full space-y-5">
        <PageHeader title="Expenses" description={admin ? "Review, approve and reimburse expenses paid out of pocket" : "Submit expenses you paid for the company and track reimbursement"}>
          <Link to="/expenses/new" className={btnPrimary}>New expense</Link>
        </PageHeader>

        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="Awaiting approval" value={String(summary.pending_approval_count)} hint={showTotal(summary.display, "pending_approval_amount", { pending_approval_amount: summary.pending_approval_amount, currency: bc })} tone="warn" />
            <StatCard label="Awaiting payment" value={String(summary.awaiting_payment_count)} hint={`${showTotal(summary.display, "outstanding", { outstanding: summary.outstanding, currency: bc })} outstanding`} />
            <StatCard label="Reimbursed" value={showTotal(summary.display, "total_reimbursed", { total_reimbursed: summary.total_reimbursed, currency: bc })} hint={`of ${showTotal(summary.display, "total_approved", { total_approved: summary.total_approved, currency: bc })} approved`} tone="success" />
            <StatCard label="Rejected" value={String(summary.rejected_count)} hint={summary.draft_count ? `${summary.draft_count} draft${summary.draft_count === 1 ? "" : "s"}` : undefined} tone="danger" />
          </div>
        )}

        <div className="flex flex-wrap items-end gap-2">
          {admin && <select className={`${inputCls} max-w-[9rem]`} value={f.scope} onChange={(e) => set("scope", e.target.value)}><option value="all">Everyone</option><option value="mine">Mine only</option></select>}
          {canListUsers && f.scope === "all" && <select className={`${inputCls} max-w-[11rem]`} value={f.user_id} onChange={(e) => set("user_id", e.target.value)}><option value="">All users</option>{users.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}</select>}
          <select className={`${inputCls} max-w-[11rem]`} value={f.status} onChange={(e) => set("status", e.target.value)}><option value="">All statuses</option>{meta?.statuses.filter((s) => admin && f.scope === "all" ? s !== "draft" : true).map((s) => <option key={s} value={s}>{s === "approved" ? "Approved · pending payment" : formatLabel(s)}</option>)}</select>
          <select className={`${inputCls} max-w-[9rem]`} value={f.category} onChange={(e) => set("category", e.target.value)}><option value="">All categories</option>{meta?.categories.map((c) => <option key={c} value={c}>{formatLabel(c)}</option>)}</select>
          <select className={`${inputCls} max-w-[7rem]`} value={f.currency} onChange={(e) => set("currency", e.target.value)}><option value="">Currency</option>{meta?.currencies.map((c) => <option key={c} value={c}>{c}</option>)}</select>
          <div className="w-40"><DatePicker value={f.date_from} onChange={(v) => set("date_from", v)} placeholder="From date" max={f.date_to || undefined} clearable aria-label="From date" /></div>
          <div className="w-40"><DatePicker value={f.date_to} onChange={(v) => set("date_to", v)} placeholder="To date" min={f.date_from || undefined} clearable aria-label="To date" /></div>
          <input className={`${inputCls} max-w-[12rem]`} placeholder="Search…" value={f.search} onChange={(e) => set("search", e.target.value)} />
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-6 animate-pulse space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-10 bg-gray-100 rounded" />)}</div>
          ) : rows.length ? (
            <TableProvider columns={columns} data={rows} onAddRow={() => navigate("/expenses/new")} addRowLabel="New expense">
              <DataTableHeader>{({ headerGroup }) => <DataTableHeaderGroup headerGroup={headerGroup}>{({ header }) => <DataTableHead header={header} />}</DataTableHeaderGroup>}</DataTableHeader>
              <DataTableBody>{({ row }) => (
                <DataTableRow row={row} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/expenses/${(row.original as Expense).id}`)}>
                  {({ cell }) => <DataTableCell cell={cell} />}
                </DataTableRow>
              )}</DataTableBody>
            </TableProvider>
          ) : (
            <p className="p-10 text-center text-sm text-gray-500">No expenses match these filters.</p>
          )}
          <Pager page={page} totalPages={pager.total_pages} total={pager.total} onChange={setPage} />
        </div>
      </div>
    </div>
  );
}
